// Recurring-expense auto-rollover. V1→V2 parity port of
// calcNextRecurDate / processRecurringExpenses in public/app/index.html:16346.
//
// V1 behaviour notes captured while porting:
//  - A "seed" is an expense with `recurring` truthy AND a `nextRecur` date.
//  - Each run compares `nextRecur` to today's ISO date (YYYY-MM-DD, lexicographic
//    compare works because of the fixed-width format).
//  - `recurUntil` is a hard cap: if `recurUntil && nextRecur > recurUntil` we
//    skip. Equality (`nextRecur === recurUntil`) still fires — the cap is
//    strictly `>`, not `>=`.
//  - Child rows do NOT inherit `recurring`/`nextRecur`/`recurUntil`. They are
//    plain expenses with `recurParent = seed.id`. The seed stays the seed.
//  - Child `notes` is the seed's notes + ` (recurring)` suffix. `paid` is
//    reset to a falsy value (V1 writes the string "no").
//  - V1's forEach only spawns ONE child per seed per run. V2 loops so a
//    long-silent seed catches up in a single pass (capped at 24 iterations
//    to guard against malformed cadence / recurUntil).
//  - Cadence strings: V1 writes "biweekly"; V2 writes "bi_weekly". We accept
//    both on read but never mutate the seed's cadence string — whatever is on
//    the wire stays there so round-trips through V1 don't churn.
//
// Helper is a pure function — caller owns state, writes, and cache patching.
//
// V1 refs:
//   public/app/index.html:16346  calcNextRecurDate
//   public/app/index.html:16366  processRecurringExpenses

import type { AppState } from '../types/firestore'
import type { Expense } from '../types/domain'
import { today as todayIsoDefault } from './fmt'

export type RecurCadenceWire = 'weekly' | 'biweekly' | 'bi_weekly' | 'monthly' | 'quarterly'
type RecurCadenceNorm = 'weekly' | 'bi_weekly' | 'monthly' | 'quarterly'

const MAX_CATCHUP_ITERATIONS = 24

/** Normalise the on-wire cadence (V1 "biweekly" → V2 "bi_weekly"). Note:
 * the Expense type doesn't list "biweekly" (V1 wire string) so we take
 * `unknown` here — V1 rows reach us with that literal at runtime. */
function normalizeCadence(r: unknown): RecurCadenceNorm | null {
  if (r === true) return 'monthly'           // V1 legacy: `true` meant monthly
  if (!r || typeof r !== 'string') return null
  if (r === 'biweekly') return 'bi_weekly'
  if (r === 'weekly' || r === 'bi_weekly' || r === 'monthly' || r === 'quarterly') return r
  return null
}

/**
 * Advance an ISO date (YYYY-MM-DD) by one cadence step.
 * Port of V1 calcNextRecurDate (public/app/index.html:16346). Accepts both the
 * V1 ("biweekly") and V2 ("bi_weekly") cadence spellings.
 */
export function calcNextRecurDate(fromIso: string, cadence: RecurCadenceWire | string): string {
  const norm = normalizeCadence(cadence)
  if (!norm) return ''
  // Noon local avoids DST boundary off-by-ones — matches V1 exactly.
  const d = new Date(fromIso + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return ''
  switch (norm) {
    case 'weekly':    d.setDate(d.getDate() + 7);  break
    case 'bi_weekly': d.setDate(d.getDate() + 14); break
    case 'monthly':   d.setMonth(d.getMonth() + 1); break
    case 'quarterly': d.setMonth(d.getMonth() + 3); break
  }
  return d.toISOString().split('T')[0]!
}

/**
 * Walk every recurring seed in `state.expenses` and spawn child rows for any
 * past-due cadence steps. Returns a NEW expenses array (don't mutate input),
 * an updated `nextExpId`, and a `changed` flag the caller can use to skip
 * writes when nothing moved.
 *
 * Idempotent: running twice in a row with no date change produces no further
 * child rows (the seed's `nextRecur` is advanced only when a child is
 * actually created).
 *
 * Safety: capped at MAX_CATCHUP_ITERATIONS per seed to protect against a
 * malformed cadence or recurUntil that would otherwise loop forever.
 */
export function catchUpRecurringSeeds(
  state: AppState,
  todayIso: string = todayIsoDefault(),
): { expenses: Expense[]; nextExpId: number; changed: boolean } {
  const source = state.expenses
  let nextExpId = state.nextExpId
  const appended: Expense[] = []
  let anySeedAdvanced = false

  // Clone seeds we touch so we don't mutate the live state array. Keep the
  // indexed position so the returned array is a stable superset of the input.
  const patched: Record<number, Expense> = {}

  for (let i = 0; i < source.length; i++) {
    const seed = source[i]!
    // Only true seeds trigger rollover. Child rows (recurParent set) are
    // skipped even if they somehow have a `recurring` field.
    if (seed.recurParent != null) continue
    const cadence = normalizeCadence(seed.recurring)
    if (!cadence) continue
    if (!seed.nextRecur) continue

    let cursor = seed.nextRecur
    let iterations = 0
    let spawnedForThisSeed = false

    while (iterations < MAX_CATCHUP_ITERATIONS) {
      // recurUntil cap — strictly `>` to match V1 (equality still fires).
      if (seed.recurUntil && cursor > seed.recurUntil) break
      if (cursor > todayIso) break

      // Skip if a child already exists for this seed+date (defensive
      // idempotency — e.g. the user ran V1 and V2 in the same session, or
      // a double-fire of this hook). Per spec: only advance the cursor
      // when we actually create a child — so if we see a dup we stop
      // here and let the next run retry.
      const already =
        source.some((e) => e.recurParent === seed.id && e.date === cursor) ||
        appended.some((e) => e.recurParent === seed.id && e.date === cursor)
      if (already) break

      // Shape mirrors V1 child row in processRecurringExpenses. Only set
      // optional fields when the seed has them (exactOptionalPropertyTypes).
      const child: Expense = {
        id: nextExpId++,
        date: cursor,
        amount: seed.amount,
        // V1 parity: public/app/index.html:16384 writes 'no' (string), not false.
        paid: 'no',
        notes: (seed.notes || '') + ' (recurring)',
        recurParent: seed.id,
        ...(seed.whoId    !== undefined ? { whoId:    seed.whoId    } : {}),
        ...(seed.whoName  !== undefined ? { whoName:  seed.whoName  } : {}),
        ...(seed.projId   !== undefined ? { projId:   seed.projId   } : {}),
        ...(seed.vendorId !== undefined ? { vendorId: seed.vendorId } : {}),
        ...(seed.payment  !== undefined ? { payment:  seed.payment  } : {}),
        ...(seed.cat      !== undefined ? { cat:      seed.cat      } : {}),
        ...(seed.type     !== undefined ? { type:     seed.type     } : {}),
      }
      appended.push(child)

      // Use the seed's on-wire cadence — leaves "biweekly" as "biweekly"
      // on the seed, while new child rows we write use V2's "bi_weekly".
      const advanced = calcNextRecurDate(cursor, seed.recurring as unknown as RecurCadenceWire)
      if (!advanced) break   // malformed — bail out safely
      cursor = advanced
      spawnedForThisSeed = true
      iterations++
    }

    if (spawnedForThisSeed && cursor !== seed.nextRecur) {
      patched[i] = { ...seed, nextRecur: cursor }
      anySeedAdvanced = true
    }
  }

  if (!anySeedAdvanced && appended.length === 0) {
    return { expenses: source, nextExpId: state.nextExpId, changed: false }
  }

  const next: Expense[] = source.map((e, i) => patched[i] ?? e)
  for (const child of appended) next.push(child)

  return { expenses: next, nextExpId, changed: true }
}
